import {
  ODSToggle,
  ODSButton,
  odsToastNotification,
  ODSToastNotificationContainer,
} from 'kfone-component-library';
import { ButtonTheme, ToastNotificationVariant } from 'kfone-component-library/enums';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useCommonStore, useSettingsStore } from 'store';
import { twMerge } from 'tailwind-merge';
import './index.css';
import { RtiReportConfiguration, type RtiReportConfigurationRef } from './rti-report-configuration';
import { TargetPayGapSettings } from './target-pay-gap-settings';
import { getPayequityToastPositionStyle, isPayequitySettingsRoute } from 'utilities/rti-toast-messege';
import { getPaySignGap, handleSettingsResponse, updateSettings } from './common-settings';

const TAB_KEYS = {
  PAY_FACTOR: 'pay-factor',
  RTI: 'rti',
} as const;

type SettingsTabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

const Settings = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const { updateSpinnerShow } = useCommonStore();
  const { isPayGapsPositive, targetPayGap } = useSettingsStore();

  const [payGapFlag, setPaygapFlag] = useState<boolean | null>(isPayGapsPositive);
  const [activeTab, setActiveTab] = useState<SettingsTabKey>(TAB_KEYS.PAY_FACTOR);
  const [rtiCanEdit, setRtiCanEdit] = useState(false);
  const [rtiFormDirty, setRtiFormDirty] = useState(false);
  const rtiReportConfigRef = useRef<RtiReportConfigurationRef>(null);

  const handleRtiEditAccessLoaded = useCallback((isEditAccess: boolean) => {
    setRtiCanEdit(isEditAccess);
  }, []);

  const fetchPaySignGap = async () => {
    getPaySignGap().then(
      (response) => {
        if (handleSettingsResponse(response)) {
          setPaygapFlag(response.data.data?.payGapFlag ? true : false);
        }
      },
      (error) => {
        console.log('error', error);
      },
    );
  };

  const putPaySignGap = async () => {
    updateSpinnerShow(true);
    updateSettings({ pay_gap_flag: !payGapFlag }).then(
      (response) => {
        if (handleSettingsResponse(response)) {
          setPaygapFlag(response.data.data?.payGapFlag);
        }
        updateSpinnerShow(false);
      },
      (error) => {
        console.log('error', error);
        updateSpinnerShow(false);
      },
    );
  };

  useEffect(() => {
    if (isPayGapsPositive === null) {
      fetchPaySignGap();
    } else {
      setPaygapFlag(isPayGapsPositive);
    }
  }, []);

  return (
    <div
      className='flex w-full flex-col p-ods-dimension-components-container-padding-large'
      id='root-library'
      data-testid='root-library'
    >
      <div className='mb-6 flex items-start justify-between gap-4'>
        <label className='report-header text-ods-font-styles-heading-medium text-[#008369]'>
          {t('pe.settings')}
        </label>
        {activeTab === TAB_KEYS.RTI ? (
        <ODSButton
          id='settings-save-changes'
          text={t('pe.settings.saveChanges')}
          theme={ButtonTheme.Primary}
          disabled={!rtiCanEdit || !rtiFormDirty}
          onClick={() => {
            if (!rtiCanEdit || !rtiFormDirty) return;
            void (async () => {
              const saved = await rtiReportConfigRef.current?.saveNow();
              if (saved !== true) return;
              window.setTimeout(() => {
                const pathNow = locationRef.current.pathname;
                if (!isPayequitySettingsRoute(pathNow)) {
                  return;
                }
                const toastStyle = getPayequityToastPositionStyle(pathNow);
                odsToastNotification({
                  description: t('pe.settings.saveSuccess'),
                  variant: ToastNotificationVariant.Success,
                  options: {
                    toastId: `settings-toast-${pathNow}`,
                    autoClose: 3000,
                    closeButton: true,
                    style: toastStyle,
                  },
                });
              }, 100);
            })();
          }}
        />
        ) : null}
      </div>

      <div className='mt-2 flex border-b border-[#E0E0E0]' role='tablist'>
        <button
          type='button'
          role='tab'
          aria-selected={activeTab === TAB_KEYS.PAY_FACTOR}
          id='tab-pay-factor-settings'
          aria-controls='panel-pay-factor-settings'
          className={twMerge(
            'relative mr-8 pb-3 text-base font-semibold text-[#6C6F75] transition-colors duration-150',
            'after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:right-0 after:block after:h-1 after:content-[""]',
            activeTab === TAB_KEYS.PAY_FACTOR
              ? 'text-[#008369] after:bg-[linear-gradient(90deg,#2f816b,#49b3bf)]'
              : 'after:bg-transparent',
          )}
          onClick={() => setActiveTab(TAB_KEYS.PAY_FACTOR)}
        >
          {t('pe.settings.tabPayFactor')}
        </button>
        <button
          type='button'
          role='tab'
          aria-selected={activeTab === TAB_KEYS.RTI}
          id='tab-rti-settings'
          aria-controls='panel-rti-settings'
          className={twMerge(
            'relative mr-8 pb-3 text-base font-semibold text-[#6C6F75] transition-colors duration-150',
            'after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:right-0 after:block after:h-1 after:content-[""]',
            activeTab === TAB_KEYS.RTI
              ? 'text-[#008369] after:bg-[linear-gradient(90deg,#2f816b,#49b3bf)]'
              : 'after:bg-transparent',
          )}
          onClick={() => setActiveTab(TAB_KEYS.RTI)}
        >
          {t('pe.settings.tabRti')}
        </button>
      </div>

      <div
        role='tabpanel'
        id={activeTab === TAB_KEYS.PAY_FACTOR ? 'panel-pay-factor-settings' : 'panel-rti-settings'}
        aria-labelledby={activeTab === TAB_KEYS.PAY_FACTOR ? 'tab-pay-factor-settings' : 'tab-rti-settings'}
        className='mt-6'
      >
        {activeTab === TAB_KEYS.PAY_FACTOR && (
          <>
            <div className='data-visual-settings'>
              <label className='text-ods-font-styles-heading-xsmall'>{t('pe.settingsHeader')}</label>
              <div className='mt-6 rounded-[5px] border border-dotted border-ods-color-brand-primary-400 px-[1.5rem] py-[1.75rem]'>
                <div className='flex items-start'>
                  <div className='flex w-[70%] flex-col font-ods-font-family-secondary text-ods-font-styles-body-regular-small text-ods-color-brand-neutral-black'>
                    <div className='mb-6 font-bold'>{t('pe.settingsPayGapTitle')}</div>
                    <div className='font-normal'>{t('pe.settingsPayGapDescription1')}</div>
                    <div className='font-normal'>{t('pe.settingsPayGapDescription2')}</div>
                  </div>
                  <div className='flex w-[30%] items-center justify-around'>
                    <label className='font-ods-font-family-secondary text-ods-font-styles-body-regular-medium font-semibold text-ods-color-brand-neutral-900'>
                      {t('pe.payGapsToggleLabel')}
                    </label>
                    <div className='flex items-center'>
                      <label className={twMerge('toggle-text', payGapFlag && 'in-active')}>
                        {t('pe.negative')}
                      </label>
                      <ODSToggle
                        id='toggle_annual_hourly'
                        onChange={putPaySignGap}
                        isToggled={payGapFlag ? true : false}
                        className='mx-2 gap-0'
                      />
                      <label className={twMerge('toggle-text', !payGapFlag && 'in-active')}>
                        {t('pe.positive')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <TargetPayGapSettings targetPayGap={targetPayGap} />
          </>
        )}
        <div className={activeTab === TAB_KEYS.RTI ? 'block' : 'hidden'} aria-hidden={activeTab !== TAB_KEYS.RTI}>
          <RtiReportConfiguration
            ref={rtiReportConfigRef}
            onRtiEditAccessLoaded={handleRtiEditAccessLoaded}
            onDirtyChange={setRtiFormDirty}
          />
        </div>
      </div>
      <ODSToastNotificationContainer />
    </div>
  );
};
export default Settings;
